import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { KeyRound, User as UserIcon, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { PassiveBadge } from "../components/ui/data-presentation";
import { OrdinaryPageBody, OrdinaryPageHeader } from "../components/ui/page-structure";

export default function Profile() {
  const { user, logout } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState({ cur: false, next: false, conf: false });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.current_password) return setErr("Enter your current password");
    if (!form.new_password || form.new_password.length < 6)
      return setErr("New password must be at least 6 characters");
    if (form.new_password !== form.confirm)
      return setErr("Password confirmation does not match");
    if (form.new_password === form.current_password)
      return setErr("New password must differ from current password");

    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password changed — sign in again");
      setForm({ current_password: "", new_password: "", confirm: "" });
      setTimeout(logout, 800);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <OrdinaryPageBody data-testid="profile-page">
      <OrdinaryPageHeader title="Profile" data-testid="profile-page-header" />
      <div className="max-w-3xl space-y-5" data-testid="profile-narrow-body">
      {/* User info */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="border-b border-zinc-200 px-6 py-4">
          <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-500 font-semibold">
            Account
          </div>
          <div className="text-base font-semibold tracking-tight">Profile</div>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="flex flex-col items-center justify-center text-center bg-zinc-50 border border-zinc-200 rounded-sm p-6">
            <div className="w-14 h-14 bg-primary text-primary-foreground flex items-center justify-center text-xl font-semibold rounded-sm">
              {user?.name?.[0] || "?"}
            </div>
            <div className="mt-3 font-medium text-sm">{user?.name}</div>
            <div className="text-[10px] tracking-[0.1em] uppercase text-zinc-500 font-semibold mt-1">
              {user?.role}
            </div>
          </div>
          <div className="md:col-span-2 space-y-4">
            <Info icon={UserIcon} label="Name" value={user?.name} />
            <Info icon={KeyRound} label="Email" value={user?.email} />
            <Info
              icon={ShieldCheck}
              label="Role"
              value={
                <PassiveBadge tone={user?.role === "admin" ? "category" : "info"} className="uppercase tracking-wider">
                  {user?.role}
                </PassiveBadge>
              }
            />
            <Info
              icon={KeyRound}
              label="Member since"
              value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
            />
          </div>
        </div>
      </div>

      {/* Change password */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="border-b border-zinc-200 px-6 py-4">
          <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-500 font-semibold">
            Security
          </div>
          <div className="text-base font-semibold tracking-tight">Change Password</div>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4" data-testid="change-password-form">
          <PasswordInput
            label="Current password"
            value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            visible={show.cur}
            toggle={() => setShow({ ...show, cur: !show.cur })}
            testid="current-password-input"
          />
          <PasswordInput
            label="New password"
            value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            visible={show.next}
            toggle={() => setShow({ ...show, next: !show.next })}
            testid="new-password-input"
          />
          <PasswordInput
            label="Confirm new password"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            visible={show.conf}
            toggle={() => setShow({ ...show, conf: !show.conf })}
            testid="confirm-password-input"
          />
          <div className="text-[11px] text-zinc-500">
            Must be at least 6 characters and different from your current password. You will
            be signed out after a successful change.
          </div>
          {err && (
            <div
            className="text-xs px-3 py-2.5 bg-semantic-danger border border-destructive/30 text-semantic-danger rounded-sm"
              data-testid="change-password-error"
            >
              {err}
            </div>
          )}
          <div className="flex justify-end pt-2 border-t border-zinc-200">
            <Button
              type="submit"
              variant="success"
              loading={loading}
              loadingLabel="Updating..."
              data-testid="change-password-submit"
            >
              Update password
            </Button>
          </div>
        </form>
      </div>
      </div>
    </OrdinaryPageBody>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 bg-zinc-100 border border-zinc-200 flex items-center justify-center text-zinc-500 rounded-sm">
        <Icon className="w-4 h-4" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-wider uppercase text-zinc-500 font-medium">
          {label}
        </div>
        <div className="text-sm font-medium text-zinc-900 break-all">{value}</div>
      </div>
    </div>
  );
}

function PasswordInput({ label, value, onChange, visible, toggle, testid }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-zinc-700 mb-1.5">
        {label}
      </div>
      <div className="relative">
        <input
          type={visible ? "text" : "password"}
          value={value}
          onChange={onChange}
          className="wms-input pr-10"
          data-testid={testid}
        />
        <button
          type="button"
          onClick={toggle}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-700"
        >
          {visible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}
